import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  ButtonGroup,
  Col,
  Modal,
  Row,
} from 'react-bootstrap';

import styles from './NotCheckedInModal.module.css';

const EMPTY_GROUP = Object.freeze({
  total: 0,
  checkedIn: 0,
  people: [],
  notCheckedIn: [],
});

const SORT_NOT_CHECKED_IN_FIRST = 'notCheckedInFirst';
const SORT_CHECKED_IN_FIRST = 'checkedInFirst';

const comparePeopleByCheckIn = (a, b, sortOrder) => {
  const aCheckedIn = Boolean(a?.hasCheckedIn);
  const bCheckedIn = Boolean(b?.hasCheckedIn);

  if (aCheckedIn !== bCheckedIn) {
    if (sortOrder === SORT_CHECKED_IN_FIRST) {
      return aCheckedIn ? -1 : 1;
    }

    return aCheckedIn ? 1 : -1;
  }

  return String(a?.name || '').localeCompare(
    String(b?.name || ''),
    undefined,
    { sensitivity: 'base' }
  );
};

const toSearchText = (person) => {
  const assignments = Array.isArray(person?.assignedBuildings)
    ? person.assignedBuildings.flatMap((building) => [building?.name, building?.id])
    : [];

  return [
    person?.name,
    person?.username,
    person?.mobile,
    person?.email,
    person?.hasCheckedIn ? 'checked in' : 'not checked in',
    person?.latestStatusDisplay,
    person?.latestStatusLabel,
    person?.latestLocation,
    ...assignments,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

const assignmentText = (person) => {
  const assignments = Array.isArray(person?.assignedBuildings)
    ? person.assignedBuildings
    : [];

  const values = assignments
    .map((building) => String(building?.name || building?.id || '').trim())
    .filter(Boolean);

  return values.length ? values.join(', ') : '—';
};

const rowClassFor = (person) => {
  if (!person?.hasCheckedIn) return 'table-danger';
  if (person?.latestStatusVariant === 'danger') return 'table-danger';
  if (person?.latestStatusVariant === 'warning') return 'table-warning';
  if (person?.latestStatusVariant === 'success') return 'table-success';
  return '';
};

function GroupProgress({ total, checkedIn }) {
  const safeTotal = Math.max(0, Number(total) || 0);
  const safeCheckedIn = Math.min(
    safeTotal,
    Math.max(0, Number(checkedIn) || 0)
  );
  const percent = safeTotal
    ? Math.round((safeCheckedIn / safeTotal) * 100)
    : 0;

  return (
    <div
      className={styles.progressWrap}
      aria-label={
        safeTotal
          ? `${safeCheckedIn} of ${safeTotal} people checked in, ${percent} percent`
          : 'No people are assigned to this group'
      }
    >
      <div
        className={`${styles.progressTrack} ${
          safeTotal ? '' : styles.progressTrackEmpty
        }`}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        {safeTotal > 0 && (
          <div
            className={styles.progressChecked}
            style={{ width: `${percent}%` }}
            aria-hidden="true"
          />
        )}

        <div className={styles.progressLabel}>
          {safeTotal > 0
            ? `${safeCheckedIn} of ${safeTotal} checked in (${percent}%)`
            : 'No assigned people'}
        </div>
      </div>
    </div>
  );
}

function GroupPanel({
  title,
  description,
  group,
  searchValue,
  onSearchChange,
  sortOrder,
  onSortOrderChange,
}) {
  const safeGroup = group || EMPTY_GROUP;
  const outstanding = Array.isArray(safeGroup.notCheckedIn)
    ? safeGroup.notCheckedIn
    : [];
  const people =
    Array.isArray(safeGroup.people) && safeGroup.people.length
      ? safeGroup.people
      : outstanding;

  const filteredAndSorted = useMemo(() => {
    const term = String(searchValue || '').trim().toLowerCase();
    const matchingPeople = term
      ? people.filter((person) => toSearchText(person).includes(term))
      : [...people];

    return matchingPeople.sort((a, b) =>
      comparePeopleByCheckIn(a, b, sortOrder)
    );
  }, [people, searchValue, sortOrder]);

  return (
    <section className={styles.groupPanel}>
      <div className={styles.groupHeader}>
        <div>
          <div className={styles.groupTitleRow}>
            <h2 className={styles.groupTitle}>{title}</h2>
            <Badge bg={outstanding.length ? 'danger' : 'success'}>
              {outstanding.length} not checked in
            </Badge>
          </div>
          <div className={styles.groupDescription}>{description}</div>
        </div>

        <GroupProgress
          total={safeGroup.total}
          checkedIn={safeGroup.checkedIn}
        />

        <div className={styles.searchSortRow}>
          <input
            type="search"
            className="form-control"
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search name, username, phone, email, status, or building…"
            aria-label={`Search ${title}`}
          />

          <ButtonGroup
            size="sm"
            className={styles.sortButtonGroup}
            aria-label={`Sort ${title}`}
          >
            <Button
              type="button"
              variant={
                sortOrder === SORT_NOT_CHECKED_IN_FIRST
                  ? 'primary'
                  : 'outline-secondary'
              }
              onClick={() => onSortOrderChange(SORT_NOT_CHECKED_IN_FIRST)}
              aria-pressed={sortOrder === SORT_NOT_CHECKED_IN_FIRST}
            >
              Not checked in
            </Button>
            <Button
              type="button"
              variant={
                sortOrder === SORT_CHECKED_IN_FIRST
                  ? 'primary'
                  : 'outline-secondary'
              }
              onClick={() => onSortOrderChange(SORT_CHECKED_IN_FIRST)}
              aria-pressed={sortOrder === SORT_CHECKED_IN_FIRST}
            >
              Checked in 
            </Button>
          </ButtonGroup>
        </div> 
      </div>

      <div className={styles.tableWrap}>
        <table className="table table-bordered table-hover table-sm align-middle mb-0">
          <thead className={styles.stickyTableHead}>
            <tr>
              <th>Name</th>
              <th>Contact</th>
              <th>Assigned buildings</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSorted.map((person) => {
              const mobile = String(person?.mobile || '').trim();
              const email = String(person?.email || '').trim();
              const username = String(person?.username || '').trim();
              const hasCheckedIn = Boolean(person?.hasCheckedIn);

              return (
                <tr
                  key={person?.userId || person?.key || person?.name}
                  className={rowClassFor(person)}
                >
                  <td>
                    <div className={styles.personNameRow}>
                      <div className={styles.personName}>
                        {person?.name || 'Unknown'}
                      </div>
                      <Badge
                        bg={hasCheckedIn ? 'success' : 'danger'}
                        className={styles.checkInBadge}
                      >
                        {hasCheckedIn ? 'Checked in' : 'Not checked in'}
                      </Badge>
                    </div>
                    {username && (
                      <div className={styles.secondaryText}>{username}</div>
                    )}
                  </td>
                  <td>
                    {mobile ? (
                      <div>
                        <a href={`tel:${mobile.replace(/[^+\d]/g, '')}`}>
                          {mobile}
                        </a>
                      </div>
                    ) : null}
                    {email ? (
                      <div>
                        <a href={`mailto:${email}`}>{email}</a>
                      </div>
                    ) : null}
                    {!mobile && !email && <span className="text-muted">—</span>}
                  </td>
                  <td>{assignmentText(person)}</td>
                </tr>
              );
            })}

            {!filteredAndSorted.length && (
              <tr>
                <td colSpan={3} className={styles.emptyCell}>
                  {people.length && String(searchValue || '').trim()
                    ? 'No people match this search.'
                    : 'No people are assigned to this group.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function NotCheckedInModal({
  show,
  onHide,
  buildingName = '',
  buildingID = '',
  exclusiveGroup = EMPTY_GROUP,
  multipleGroup = EMPTY_GROUP,
}) {
  const [exclusiveSearch, setExclusiveSearch] = useState('');
  const [multipleSearch, setMultipleSearch] = useState('');
  const [exclusiveSortOrder, setExclusiveSortOrder] = useState(
    SORT_NOT_CHECKED_IN_FIRST
  );
  const [multipleSortOrder, setMultipleSortOrder] = useState(
    SORT_NOT_CHECKED_IN_FIRST
  );

  useEffect(() => {
    if (!show) return;
    setExclusiveSearch('');
    setMultipleSearch('');
    setExclusiveSortOrder(SORT_NOT_CHECKED_IN_FIRST);
    setMultipleSortOrder(SORT_NOT_CHECKED_IN_FIRST);
  }, [show, buildingID]);

  const buildingLabel =
    String(buildingName || '').trim() ||
    String(buildingID || '').trim() ||
    'Active incident building';

  const totalOutstanding =
    (exclusiveGroup?.notCheckedIn?.length || 0) +
    (multipleGroup?.notCheckedIn?.length || 0);

  return (
    <Modal
      show={show}
      onHide={onHide}
      fullscreen
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 w-100 me-3">
          <div>
            <Modal.Title>Not Checked In And Other Details — {buildingLabel}</Modal.Title>
            <div className="text-muted small">
              These lists update automatically as people check in.
            </div>
          </div>
          <Badge bg={totalOutstanding ? 'danger' : 'success'} className="fs-6">
            {totalOutstanding} person(s) outstanding
          </Badge>
        </div>
      </Modal.Header>

      <Modal.Body className={styles.modalBody}>
        <Row className={`${styles.groupsRow} g-3`}>
          <Col lg={6} className={styles.groupColumn}>
            <GroupPanel
              title={`Assigned only to ${buildingLabel}`}
              description={`People whose only assigned building is ${buildingLabel}.`}
              group={exclusiveGroup}
              searchValue={exclusiveSearch}
              onSearchChange={setExclusiveSearch}
              sortOrder={exclusiveSortOrder}
              onSortOrderChange={setExclusiveSortOrder}
            />
          </Col>

          <Col lg={6} className={styles.groupColumn}>
            <GroupPanel
              title={`Assigned to ${buildingLabel} and other buildings`}
              description={`People assigned to ${buildingLabel} who also have one or more additional building assignments.`}
              group={multipleGroup}
              searchValue={multipleSearch}
              onSearchChange={setMultipleSearch}
              sortOrder={multipleSortOrder}
              onSortOrderChange={setMultipleSortOrder}
            />
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
